import React from "react";

import { WagtailImageRendition } from "@reactivated";

import { groupBy, sortBy } from "../../utils/functional";
import { concatClassNames } from "../../utils/styles";

import styles from "./index.module.scss";

interface IProps {
    renditions: WagtailImageRendition[];
    /** Attributes are set on the picture element */
    attrs?: React.HTMLAttributes<HTMLPictureElement>;
    /** Attributes are set on the image element */
    imageAttrs?: React.ImgHTMLAttributes<HTMLImageElement>;
}

const getRenditionGroupAvgSize = (group: Array<{ size: number }>): number => {
    if (group.length <= 0) {
        return 0;
    }
    const totalSize = group.reduce<number>(
        (memo, rendition) => memo + rendition.size,
        0,
    );
    return totalSize / group.length;
};

export const ImageRenditionSet = (props: IProps) => {
    const attrs = props.attrs || {};
    attrs.className = concatClassNames([attrs.className, styles.picture]);
    const imageAttrs = props.imageAttrs || {};
    imageAttrs.className = concatClassNames([imageAttrs.className, styles.img]);

    // Abort if we have nothing to show
    if (props.renditions.length <= 0) {
        return null;
    }

    // Sort by size, then by mimetype, then group by mimetype
    let renditions = sortBy(props.renditions, "size");
    renditions = sortBy(renditions, "mimetype");
    let groupedRenditions = [...groupBy(renditions, "mimetype")];

    // Sort each mime group by its average size
    groupedRenditions = groupedRenditions.sort((groupA, groupB) => {
        const avgA = getRenditionGroupAvgSize(groupA);
        const avgB = getRenditionGroupAvgSize(groupB);
        if (avgA === avgB) {
            return 0;
        }
        return avgA > avgB ? 1 : -1;
    });

    // For the fallback image, use the smallest version of the oldest file format.
    const defaultSrc = groupedRenditions[groupedRenditions.length - 1][0].url;

    return (
        <picture {...attrs}>
            {groupedRenditions.map((renditions) => {
                const mime = renditions[0].mimetype;
                const srcSet = renditions
                    .map((r) => `${r.url} ${r.width}w`)
                    .join(", ");
                return <source key={mime} type={mime} srcSet={srcSet} />;
            })}
            <img src={defaultSrc} loading="lazy" {...imageAttrs} />
        </picture>
    );
};
